import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import { useCallback, useState } from 'react';
import { Import, useImportContext } from 'src/components/import';
import { ParsedRecord } from 'src/components/import/ImportParser';
import { fetchPjmLocationsList, usePjmRegistrationCreateMutation } from 'src/domain/pjm/queries';
import { mapCsvImportRegistrationToCreateRegistrationDto } from 'src/domain/pjm/import/mapCsvImportRegistrationToCreateRegistrationDto';
import { PjmRegistrationCsvColumns, pjmRegistrationCsvConfig, pjmRegistrationParser } from 'src/domain/pjm/import/pjmRegistrationParser';

interface IPjmRegistrationsImportModalProps {
    onClose: (actionTaken: boolean) => void;
}

const PjmRegistrationsImportModalWrapped = ({ onClose }: IPjmRegistrationsImportModalProps) => {
    const {
        viewState: { view, records },
        setImportProgress,
        importErrors,
        setImportErrors,
        importHasErrors,
    } = useImportContext();

    const [loading, setLoading] = useState(false);

    const pjmRegistrationCreateMutation = usePjmRegistrationCreateMutation();

    const handleImportSubmit = useCallback(
        async (records: ParsedRecord<PjmRegistrationCsvColumns>[]) => {
            if (!records.length) return;

            const total = records.length;
            let imported = 0;
            let failed = 0;

            const updateProgress = () => setImportProgress({ imported, failed, total });
            updateProgress();

            setLoading(true);
            const locationIds = records.map(r => r['Location ID*'].parsedValue);
            const locationsResponse = await fetchPjmLocationsList({ filter: { id: locationIds }, pagination: { pageSize: 10000 } });
            const locationsMap = new Map(locationsResponse.data.map(location => [location.id, location]));

            for (let row = 0; row < records.length; row++) {
                try {
                    const record = records[row];
                    const locationId = record['Location ID*'].parsedValue;
                    const location = locationsMap.get(locationId);

                    if (!location) {
                        throw new Error(`Location with ID=${locationId} not found!`);
                    }

                    const dto = mapCsvImportRegistrationToCreateRegistrationDto(record, location);
                    await pjmRegistrationCreateMutation.mutateAsync(dto);
                    imported++;
                } catch (error: any) {
                    failed++;
                    importErrors[row] = error.message || 'Import failed!';
                    setImportErrors(importErrors);
                }
                updateProgress();
            }

            setLoading(false);
        },
        [pjmRegistrationCreateMutation, setImportProgress, importErrors, setImportErrors]
    );

    const handleImportCancel = () => {
        const actionTaken = !!records.length;
        onClose(actionTaken);
    };

    const modalActions: JSX.Element[] = [];

    if (importHasErrors) {
        modalActions.push(<Import.DownloadErrorsButton key="import-download-errors" />);
    }

    modalActions.push(
        <Button key="import-cancel-button" disabled={loading} onClick={handleImportCancel}>
            Close
        </Button>,
        <Import.SubmitButton<PjmRegistrationCsvColumns> key="import-submit-button" onClick={handleImportSubmit} />
    );

    return (
        <Modal
            title="Import PJM Registrations"
            width="80%"
            open
            keyboard={false}
            destroyOnHidden
            maskClosable={false}
            onCancel={handleImportCancel}
            footer={modalActions}
        >
            <div>
                {view === 'initial' && <Import.UploadZone acceptFileType=".csv" />}
                {view === 'initial' && <Import.ImportTemplate templateName="import-pjm-registrations-template" />}
                {view === 'initial' && <Import.FileDescription />}

                {['preview', 'importing', 'completed'].includes(view) && <Import.Preview<PjmRegistrationCsvColumns> />}

                {(view === 'importing' || view === 'completed') && <Import.Progress />}

                {view === 'completed' && <Import.CompletedMessage />}
            </div>
        </Modal>
    );
};

export const PjmRegistrationsImportModal = (props: IPjmRegistrationsImportModalProps) => {
    return (
        <Import.Provider<PjmRegistrationCsvColumns> csvConfig={pjmRegistrationCsvConfig} parser={pjmRegistrationParser}>
            <PjmRegistrationsImportModalWrapped {...props} />
        </Import.Provider>
    );
};
